import { useState } from "react";
import { useT } from "@/lib/i18n";
import {
  type CheckpointResult,
  type ScenarioSummary,
  useAcceptBorderline,
  useCheckpoint,
  useScenario,
  useScenarios,
  useSubmitCheckpoint,
} from "@/lib/scenarios";
import { CheckpointItem } from "./CheckpointItem";
import { CheckpointTopBar } from "./CheckpointTopBar";
import { ResultBorderline } from "./results/ResultBorderline";
import { ResultFailed } from "./results/ResultFailed";
import { ResultPassed } from "./results/ResultPassed";

type CheckpointControllerProps = {
  slug: string;
  // Where to send the user after they finish. Each branch may pick
  // its own destination — the controller just calls back.
  onExit: () => void;
  onResumeSession: () => void;
};

/**
 * Top-level orchestrator for the checkpoint exam.
 *
 * Flow:
 *   1. Fetch checkpoint items + scenario detail in parallel.
 *   2. Walk through items one-by-one (no back-nav, no skip).
 *   3. After last item, POST `submit` with all answers, get
 *      `CheckpointResult`.
 *   4. Branch on `outcome`:
 *      - PASSED → ResultPassed (with newly-unlocked scenarios)
 *      - BORDERLINE → ResultBorderline (accept / repeat choice)
 *      - FAILED → ResultFailed (resume / break choice)
 *
 * No exit confirmation dialog yet — Phase 7 polish. For now the X
 * button just navigates away; mid-quiz answers are kept in component
 * state so a navigate-back-then-forward via the browser history
 * preserves them, but a hard reload starts over.
 */
export function CheckpointController({ slug, onExit, onResumeSession }: CheckpointControllerProps) {
  const t = useT();
  const checkpoint = useCheckpoint(slug);
  const scenario = useScenario(slug);
  const scenarios = useScenarios();
  const submit = useSubmitCheckpoint(slug);
  const acceptBorderline = useAcceptBorderline(slug);

  // Local answers per item slug; advanced as the user moves through.
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [cursor, setCursor] = useState(0);
  const [result, setResult] = useState<CheckpointResult | null>(null);

  // Result branch goes FIRST. Once `submit` resolves, we hold the
  // outcome in local state and the result screen owns the view —
  // even if the underlying status transition (READY_FOR_CHECKPOINT
  // → COMPLETED/IN_PROGRESS) makes the checkpoint query 400 on its
  // post-submit refetch, we don't fall back to the empty UI.
  if (result && scenario.data) {
    if (result.outcome === "PASSED") {
      // Newly-unlocked = scenarios in the journal that listed `slug`
      // as a prerequisite AND now show as AVAILABLE for this user
      // (i.e. the user just unlocked them).
      const unlocked: ScenarioSummary[] = (scenarios.data ?? []).filter(
        (s) => s.prerequisiteSlugs.includes(slug) && s.progress?.status === "AVAILABLE",
      );
      return <ResultPassed scenario={scenario.data} result={result} unlocked={unlocked} onDone={onExit} />;
    }
    if (result.outcome === "BORDERLINE") {
      return (
        <ResultBorderline
          scenario={scenario.data}
          result={result}
          onAccept={() => {
            acceptBorderline.mutate(undefined, {
              onSuccess: () => onExit(),
            });
          }}
          onRepeat={onResumeSession}
        />
      );
    }
    return <ResultFailed scenario={scenario.data} result={result} onResume={onResumeSession} onBreak={onExit} />;
  }

  // No result yet — render the quiz UI. Loading and error guards
  // come below the result branch so a post-submit error refetch
  // doesn't kick the user out of the result screen.
  if (checkpoint.isLoading || scenario.isLoading) {
    return (
      <div className="flex min-h-[calc(100dvh-80px)] items-center justify-center text-sm text-text-3">
        {t("skills.checkpoint_loading")}
      </div>
    );
  }
  if (
    checkpoint.isError ||
    !checkpoint.data ||
    scenario.isError ||
    !scenario.data ||
    checkpoint.data.items.length === 0
  ) {
    return (
      <div className="flex min-h-[calc(100dvh-80px)] flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-text-3">{t("skills.checkpoint_empty")}</p>
        <button type="button" onClick={onExit} className="text-[13px] text-text-3 hover:text-text">
          {t("skills.locked_back")}
        </button>
      </div>
    );
  }

  const data = checkpoint.data;
  const total = data.items.length;
  const item = data.items[cursor]!;

  const onItemSubmit = (given: string) => {
    const nextAnswers = { ...answers, [item.slug]: given };
    setAnswers(nextAnswers);
    if (cursor + 1 >= total) {
      // Last item — submit the whole batch.
      submit.mutate(
        Object.entries(nextAnswers).map(([itemSlug, value]) => ({ itemSlug, given: value })),
        {
          onSuccess: (res) => setResult(res),
        },
      );
    } else {
      setCursor(cursor + 1);
    }
  };

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-3.5">
      <CheckpointTopBar scenarioTitle={scenario.data.title} currentIndex={cursor} totalItems={total} onClose={onExit} />
      {/* `key={slug}` forces a fresh component instance per item so
          local state (`picked`, `typed`) doesn't leak across items —
          otherwise the next item inherits the previous click's
          disabled-options state. */}
      <CheckpointItem key={item.slug} item={item} initialAnswer={answers[item.slug]} onSubmit={onItemSubmit} />
      {submit.isPending && <div className="text-center font-mono text-[11px] text-text-3">…</div>}
    </div>
  );
}
